import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { THEMED_MENUS } from "@/lib/content";

export default function ThemedMenus() {
  const [idx, setIdx] = useState(0);
  const menu = THEMED_MENUS[idx];

  return (
    <section
      id="themed-menus"
      className="relative bg-surface py-28 md:py-40 border-t border-white/10"
      data-testid="themed-menus"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <Overline>Themed Menus · Good food is personal</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-ink text-4xl md:text-6xl lg:text-7xl leading-[1] max-w-4xl">
            Whatever the <em className="italic text-metallic-gold">occasion</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 font-sans text-base md:text-lg text-ink/70 max-w-2xl leading-relaxed">
            Wellness isn&rsquo;t the whole story. We cook the comfort food you crave —
            burgers, hand-made pasta, kids&rsquo; favourites and long
            BBQ lunches — with the same care and beautiful sourcing as everything else.
          </p>
        </Reveal>

        {/* Menu tabs */}
        <div className="mt-14 md:mt-16 flex flex-wrap gap-3">
          {THEMED_MENUS.map((m, i) => (
            <button
              key={m.tag}
              onClick={() => setIdx(i)}
              data-testid={`themed-menu-tab-${m.tag.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-6 py-2.5 font-sans text-xs tracking-[0.2em] uppercase transition-all duration-500 ${
                idx === i
                  ? "border-gold text-metallic-gold bg-black/40"
                  : "border-white/15 text-ash hover:text-metallic-gold hover:border-gold/50"
              }`}
            >
              {m.tag}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={menu.tag}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16"
          >
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden aspect-[4/5]">
                <img
                  src={menu.image}
                  alt={`${menu.tag} menu`}
                  className="h-full w-full object-cover img-cinematic"
                />
                <div className="absolute inset-0 border border-white/10 pointer-events-none" />
              </div>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-ink/85 leading-snug max-w-md">
                {menu.note}
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="divide-y divide-white/10 border-t border-white/10">
                {menu.items.map((it) => (
                  <div key={it.name} className="py-6">
                    <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">
                      {it.name}
                    </h3>
                    <p className="mt-2 font-sans text-sm md:text-base text-ash leading-relaxed max-w-2xl">
                      {it.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
